import { useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
} from '@fluentui/react-components';
import {
  ArrowMove20Regular,
  Delete20Regular,
  DismissCircle20Regular,
  MailRead20Regular,
  MailUnread20Regular,
} from '@fluentui/react-icons';
import { useSetMailRead } from '@/hooks/useSetMailRead';
import { useNotify } from '@/features/notifications/useNotify';
import { useItemSelectionStore } from '@/stores/itemSelectionStore';
import { MoveManyDialog } from './MoveManyDialog';
import { useBatchActions, type BatchResult } from './useBatchActions';
import { summarizeBatch, type SelectedRef } from './selection';

/**
 * LE MENU CONTEXTUEL D'UNE MULTI-SÉLECTION.
 *
 * Quand plusieurs éléments sont sélectionnés, le menu de la ligne cliquée ne peut
 * pas rester celui d'UN élément : « Renommer », « Ouvrir », « Copier le lien » ne
 * veulent rien dire pour quatre lignes, et surtout ils agiraient sur une seule —
 * l'utilisateur croirait avoir traité sa sélection. Le menu est donc REMPLACÉ, pas
 * complété, et il annonce le nombre d'éléments visés dans chaque libellé.
 *
 * Ce qui reste possible sur un lot, et rien d'autre :
 *  • déplacer (dossiers ET mails, avec compte rendu partiel) ;
 *  • marquer lu / non lu (les mails du lot) ;
 *  • supprimer ;
 *  • abandonner la sélection — sortie explicite, parce qu'un clic simple ailleurs
 *    est la seule autre façon d'y arriver et que ce n'est pas devinable.
 *
 * LU ET NON LU SONT DEUX ENTRÉES, pas une bascule : sur un lot mélangeant des
 * mails lus et non lus, une bascule devrait choisir un sens à la place de
 * l'utilisateur, et l'inverse de ce qu'il attend une fois sur deux.
 *
 * Rend `items` (les entrées) ET `dialogs` séparément : un `<Dialog>` posé dans un
 * `<MenuList>` serait démonté avec le menu à la première interaction, donc ne
 * s'afficherait jamais.
 */
export function useMultiSelectionMenu({
  items,
  onDone,
}: {
  items: SelectedRef[];
  onDone?: () => void;
}): { isMulti: boolean; items: ReactNode; dialogs: ReactNode } {
  const [moving, setMoving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pending, setPending] = useState(false);
  const { move, remove } = useBatchActions();
  const setRead = useSetMailRead();
  const { notify } = useNotify();
  const clear = useItemSelectionStore((s) => s.clear);

  const isMulti = items.length > 1;
  const mails = items.filter((i) => i.kind === 'mail');

  /** Compte rendu commun : l'intention suit ce qui a RÉELLEMENT échoué. */
  const report = ({ outcomes, carried }: BatchResult, verb: string) => {
    const failed = outcomes.filter((o) => o.status === 'error').length;
    const done = outcomes.filter((o) => o.status === 'done').length;
    notify(
      failed > 0 ? (done > 0 ? 'warning' : 'error') : 'success',
      failed > 0 ? 'Terminé en partie' : 'Terminé',
      summarizeBatch(outcomes, verb, carried),
    );
  };

  const runMove = async (dest: { folderId: string | null; containerId: string }) => {
    setPending(true);
    try {
      const result = await move({
        refs: items,
        targetFolderId: dest.folderId,
        targetContainerId: dest.containerId,
      });
      report(result, 'déplacé');
      // La sélection est ABANDONNÉE après coup : ses éléments ne sont plus là où
      // ils étaient, et la garder ferait porter l'action suivante sur des lignes
      // qui ont bougé sous les yeux de l'utilisateur.
      clear();
    } finally {
      setPending(false);
      setMoving(false);
    }
  };

  const runDelete = async () => {
    setPending(true);
    try {
      report(await remove(items), 'supprimé');
      clear();
    } finally {
      setPending(false);
      setConfirmDelete(false);
    }
  };

  const markAll = (read: boolean) => {
    onDone?.();
    // Chaque mail passe par le même chemin qu'un marquage unitaire : optimiste,
    // sans attente, et sans toast. Vingt toasts pour un geste seraient pires que
    // pas de retour du tout — et l'effet est visible immédiatement (la pastille).
    for (const m of mails) setRead(m.id, read);
    clear();
  };

  if (!isMulti) return { isMulti: false, items: null, dialogs: null };

  const menuItems = (
    <>
      <MenuGroup>
        <MenuGroupHeader>{items.length} éléments sélectionnés</MenuGroupHeader>
        <MenuItem
          icon={<ArrowMove20Regular />}
          onClick={() => {
            onDone?.();
            setMoving(true);
          }}
        >
          Déplacer vers…
        </MenuItem>
        {mails.length > 0 && (
          <>
            <MenuItem icon={<MailRead20Regular />} onClick={() => markAll(true)}>
              Marquer {mails.length} mail{mails.length > 1 ? 's' : ''} comme lu
              {mails.length > 1 ? 's' : ''}
            </MenuItem>
            <MenuItem icon={<MailUnread20Regular />} onClick={() => markAll(false)}>
              Marquer comme non lu{mails.length > 1 ? 's' : ''}
            </MenuItem>
          </>
        )}
        <MenuItem
          icon={<DismissCircle20Regular />}
          onClick={() => {
            onDone?.();
            clear();
          }}
        >
          Tout désélectionner
        </MenuItem>
      </MenuGroup>
      {/* Isolée par un séparateur, comme dans les menus unitaires : la
          suppression ne doit pas être à un geste de distraction d'un déplacement. */}
      <MenuDivider />
      <MenuItem
        icon={<Delete20Regular />}
        onClick={() => {
          onDone?.();
          setConfirmDelete(true);
        }}
      >
        Supprimer {items.length} éléments
      </MenuItem>
    </>
  );

  const dialogs = (
    <>
      {moving && (
        <MoveManyDialog
          items={items}
          pending={pending}
          onClose={() => setMoving(false)}
          onConfirm={(dest) => void runMove(dest)}
        />
      )}
      <Dialog open={confirmDelete} onOpenChange={(_e, d) => setConfirmDelete(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Supprimer {items.length} éléments ?</DialogTitle>
            <DialogContent>
              {/* Le décompte est détaillé parce que les deux natures n'ont pas la
                  même portée : supprimer un dossier masque aussi tout ce qu'il
                  contient, ce que le nombre d'éléments cochés ne laisse pas voir. */}
              {mails.length > 0 && `${mails.length} mail(s)`}
              {mails.length > 0 && items.length - mails.length > 0 && ' et '}
              {items.length - mails.length > 0 &&
                `${items.length - mails.length} dossier(s), avec leur contenu,`}{' '}
              seront supprimés. Récupération possible pendant 30 jours via la corbeille.
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setConfirmDelete(false)}
                disabled={pending}
              >
                Annuler
              </Button>
              <Button appearance="primary" disabled={pending} onClick={() => void runDelete()}>
                {pending ? 'Suppression…' : 'Supprimer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );

  return { isMulti, items: menuItems, dialogs };
}
